import type { TimeEntry } from './types'

/**
 * The shortcuts offered above the week grid: the last few things a person
 * logged, ready to log again.
 */

/**
 * The most recent *distinct* entries, newest first.
 *
 * Distinct matters. Somebody who logs the same standup every morning would
 * otherwise get five identical cards and no shortcuts at all, which is the
 * opposite of what the list is for. Two entries are the same shortcut when
 * copying one would produce the other: same project, same tags, same
 * minutes, same note.
 *
 * The tag set is compared as a set, not a list — the order the join table
 * happened to return them in is not a difference anybody would recognise.
 *
 * `entries` must already be newest-first; the caller's query decides what
 * "recent" means, and this only thins the list.
 */
export function distinctRecent(entries: readonly TimeEntry[], limit: number): TimeEntry[] {
  const seen = new Set<string>()
  const picked: TimeEntry[] = []

  for (const entry of entries) {
    if (picked.length >= limit) break

    const key = JSON.stringify([
      entry.projectId,
      [...entry.tagIds].sort(),
      entry.minutes,
      entry.notes ?? '',
    ])

    if (seen.has(key)) continue
    seen.add(key)
    picked.push(entry)
  }

  return picked
}

/**
 * A shortcut somebody chose to keep.
 *
 * A pin is a copy of what was logged, not a pointer at the entry: the entry
 * it came from can be edited or deleted and the shortcut still means what
 * the person meant when they pinned it. `pinnedAt` is when they pressed the
 * button, which is what orders the list.
 */
export interface Pinned {
  projectId: string
  tagIds: string[]
  minutes: number
  notes: string | null
  pinnedAt: string
}

/**
 * What makes two shortcuts "the same" for the purpose of the list.
 *
 * Deliberately coarser than the de-duplication above: project and duration
 * only. Somebody who has pinned "Front End, 2h" does not want the same two
 * hours offered again underneath just because that day's entry carried a
 * tag or a note. The pin wins and the recent entry drops out.
 */
export function pinKey(shortcut: { projectId: string; minutes: number }): string {
  return `${shortcut.projectId}|${shortcut.minutes}`
}

/**
 * Pins in the order they appear at the top of the list: oldest first, so a
 * long-standing shortcut keeps its place and a new pin lands at the bottom
 * of the pinned group rather than shuffling everything down.
 *
 * Ties break on project then minutes, so two pins made in the same second
 * still have a stable order.
 */
export function orderPins(pins: readonly Pinned[]): Pinned[] {
  return [...pins].sort(
    (a, b) =>
      a.pinnedAt.localeCompare(b.pinnedAt) ||
      a.projectId.localeCompare(b.projectId) ||
      a.minutes - b.minutes,
  )
}

/** The recent entries that are not already offered as a pin. */
export function withoutPinned(
  entries: readonly TimeEntry[],
  pins: readonly Pinned[],
): TimeEntry[] {
  const pinned = new Set(pins.map(pinKey))
  return entries.filter((entry) => !pinned.has(pinKey(entry)))
}
